import TopicView from '../components/Topic'
import { components } from '@rtidatascience/harness-ui'
import TableChart from '../components/TableChart'
import GraphChart from '../components/GraphChart'
import AccordionFilter from '../components/AccordionFilter'
import { json } from 'd3-fetch'
import { commitMetadata } from '../store/mutations/selectedParameters'
import { TaylorWRSurvey } from '../vest'

const hashes = {
  2014: {
    puf: 'ac55ec37',
    metadata: 'b809a909',
    design: '23c821a9'
  },
  2022: {
    puf: '3cc90034',
    metadata: '70e706f5',
    design: 'bf9ef903'
  }
}

export default class TopicPage {
    title = 'Topics'
    key = 'topic'
    pageComponent = TopicView

    retrieveData = async (state, pageObject, hs) => {
      const data = await loadData(hs)

      if (data.metadata !== hs.store.state.metadata) {
        commitMetadata(hs.store.commit, data.metadata)
        hs.setOptionsForFilter(
          'accordion',
          {
            groups: hs.store.state.modules,
            metadata: data.metadata
          },
          true
        )
      }

      const survey = createSurvey(data.surveyData, data.surveyDesign)
      const filteredSurvey = filterSurvey(survey, hs.store.state.selectedFilter)
      const tabledData = buildTable(
        filteredSurvey,
        data.metadata,
        hs.store.state.selectedRowParameters,
        hs.store.state.selectedColumnParameters,
        hs.store.state.selectedFilter,
        hs.store.getters.year
      )
      const footnotes = buildFootnotes(
        tabledData.table,
        hs.store.getters.rowQuestion,
        hs.store.getters.columnQuestion,
        hs.store.getters.filterQuestion,
        hs.store.getters.year
      )

      hs.store.commit('setIsLoadingYear', false)

      return {
        tableChart: {
          ...tabledData,
          footnotes
        },
        graphChart: {
          ...buildGraph(
            tabledData,
            data.metadata,
            hs.store.state.selectedRowParameters,
            hs.store.state.selectedColumnParameters
          ),
          footnotes
        },
        accordion: {
          groups: hs.store.state.modules,
          metadata: data.metadata
        }
      }
    }

    filters = function () {
      return {
        year: {
          key: 'year',
          label: 'Data Year',
          component: null,
          options: []
        },
        accordion: {
          title: 'Accordion',
          component: AccordionFilter,
          props: {
            getValueProperties (id) {
              return this.$store.getters.getQuestion(id)
            }
          },
          afterSet: (action, hs) => {
            if (action.type === hs.getFilterActionString('accordion')) {
              hs.setOptionsForFilter(
                'questionLevels',
                extractQuestionLevels(action, hs),
                true
              )
            }
          }
        },
        questionLevels: {
          key: 'questionLevels',
          label: 'Levels',
          component: components.HarnessUiCheckboxGroup,
          props: {
            multiple: true,
            collapse: false
          },
          options: [
            {
              key: 'q1',
              label: 'Level 1',
              default: true
            }
          ],
          afterSet: (action, hs) => {
            if (action.type === hs.getFilterActionString('questionLevels')) {
              // hs.store.commit('buildFilter', action.payload)
            }
          }
        },
        rowFilter: {
          key: 'rowFilter',
          label: 'Levels',
          component: null,
          options: []
        },
        columnFilter: {
          key: 'columnFilter',
          label: 'Levels',
          component: null,
          options: []
        },
        confidenceInterval: {
          label: 'Confidence Intervals:',
          component: components.HarnessUiRadioGroup,
          props: {
            inline: false
          },
          options: [
            {
              key: true,
              label: 'On',
              disabled: false,
              default: false
            },
            {
              key: false,
              label: 'Off',
              disabled: false,
              default: true
            }
          ]
        },
        showGraph: {
          label: 'View As:',
          component: components.HarnessUiRadioGroup,
          props: {
            inline: false
          },
          options: [
            {
              key: true,
              label: 'Chart',
              disabled: false,
              default: true
            },
            {
              key: false,
              label: 'Table',
              disabled: false,
              default: false
            }
          ],
          afterSet: (action, hs) => {
            if (action.payload) {
              hs.enableOptions('showPercentage', [true, false])
            } else {
              hs.disableOptions('showPercentage', [true, false])
            }
          }
        },
        showPercentage: {
          label: 'Chart Unit:',
          component: components.HarnessUiRadioGroup,
          props: {
            inline: false
          },
          options: [
            {
              key: true,
              label: 'Percentage',
              disabled: false,
              default: true
            },
            {
              key: false,
              label: 'Population Size',
              disabled: false,
              default: false
            }
          ]
        }
      }
    }

    charts = function () {
      return {
        tableChart: {
          title: 'Table Chart',
          component: TableChart
        },
        graphChart: {
          title: 'Graph Chart',
          component: GraphChart
        }
      }
    }
}

const dataSource = 'Scripts/hcps/data/'

function variableFilePath (id, year) {
  return `${process.env.BASE_URL}${dataSource}${year}/puf_${hashes[year].puf}/${id}.json`
}

function metadataFilePath (year) {
  return `${process.env.BASE_URL}${dataSource}${year}/metadata.${hashes[year].metadata}.json`
}

function designFilePath (year) {
  return `${process.env.BASE_URL}${dataSource}${year}/design.${hashes[year].design}.json`
}

async function loadData (hs) {
  const cache = hs.getRequestCache()
  const year = hs.store.getters.year
  const yearCache = cache?.[year]

  const metadata = await loadMetadata(yearCache, year)
  const surveyDesign = await loadSurveyDesign(yearCache, year)
  const surveyData = await loadSurveyData(hs, surveyDesign, yearCache, year)

  const data = {
    metadata,
    surveyDesign,
    surveyData
  }

  const newCache = {
    ...cache,
    [year]: data
  }
  hs.setRequestCache(newCache)

  return data
}

async function loadSurveyData (hs, surveyDesign, cache) {
  const { rowId, columnId, filterId, year } = hs.store.getters

  const weight = await loadVariableFile(cache?.surveyData, surveyDesign.weight, year)
  const stratum = await loadVariableFile(cache?.surveyData, surveyDesign.stratum, year)
  const cluster = await loadVariableFile(cache?.surveyData, surveyDesign.cluster, year)
  const row = await loadVariableFile(cache?.surveyData, rowId, year)
  const col = await loadVariableFile(cache?.surveyData, columnId, year)
  const filter = await loadVariableFile(cache?.surveyData, filterId, year)

  // Add special variables that we'll use when computing estimates.
  const dataLength = countData(weight)
  const zero = createConstantVariable('__zero__', '0', dataLength)
  const subpop = createConstantVariable('__subpop__', true, dataLength)

  return {
    ...weight,
    ...stratum,
    ...cluster,
    ...row,
    ...col,
    ...filter,
    ...zero,
    ...subpop
  }
}

function createConstantVariable (id, value, dataLength) {
  const values = new Array(dataLength)
  for (let i = 0; i < dataLength; i++) {
    values[i] = value
  }
  return { [id]: values }
}

async function loadVariableFile (cache, id, year) {
  if (id == null) {
    return {}
  }

  if (cache != null && cache[id]) {
    return { [id]: cache[id] }
  }

  const file = await json(variableFilePath(id, year))
  return { [id]: file.data }
}

function countData (data) {
  const sampleKey = Object.keys(data)[0]
  return data[sampleKey].length
}

function createSurvey (surveyData, surveyDesign) {
  const survey = new TaylorWRSurvey(
    surveyData,
    surveyDesign.weight,
    surveyDesign.stratum,
    surveyDesign.cluster,
    surveyDesign.ddf
  )

  return survey
}

async function loadMetadata (cache, year) {
  if (cache != null && cache.metadata != null) {
    return cache.metadata
  }

  const metadata = await json(metadataFilePath(year))
  return processMetadata(metadata)
}

async function loadSurveyDesign (cache, year) {
  if (cache != null && cache.surveyDesign != null) {
    return cache.surveyDesign
  }

  const surveyDesign = await json(designFilePath(year))
  return surveyDesign
}

function processMetadata (metadata) {
  for (const key in metadata) {
    metadata[key].id = key
  }

  return metadata
}

function extractQuestionLevels (action, hs) {
  if (action.payload == null) {
    return []
  } else {
    return hs.store.getters.getQuestion(action.payload).levels.map(l => ({ key: l.value, label: l.label, default: true }))
  }
}

function filterSurvey (survey, filter) {
  // For survey data, it's important not to remove data when filtering. Instead,
  // we should flag the data to include/exclude.
  const subpopData = survey.data.__subpop__

  if (filter === null) {
    for (let i = 0; i < subpopData.length; i++) {
      subpopData[i] = true
    }
  } else {
    const filterData = survey.data[filter.id]
    for (let i = 0; i < subpopData.length; i++) {
      subpopData[i] = filter.values.includes(filterData[i])
    }
  }

  return survey
}

export function suppressCell (cell) {
  if (cell.unweightedDenominator < 50 || cell.rseProportion > 0.3) {
    return {
      suppressed: true,
      proportion: null,
      ciProportion: { lower: null, upper: null },
      varianceProportion: null,
      sampleSize: null,
      unweightedDenominator: null,
      numerator: null,
      ciNumerator: { lower: null, upper: null },
      varianceNumerator: null,
      denominator: null,
      rseProportion: null
    }
  } else {
    return {
      suppressed: false,
      ...cell
    }
  }
}

function setCellHasResult (cell) {
  return {
    noResult: cell.unweightedDenominator === 0
  }
}

function buildTable (survey, metadata, rowFilter, columnFilter, subpopFilter, year) {
  if (rowFilter == null) {
    return { table: [], title: '', subtitle: '' }
  }

  let subpopTitleClause = ''
  let subpopSubtitleClause = ''
  if (subpopFilter !== null) {
    const meta = metadata[subpopFilter.id]
    const valueLabels = subpopFilter.values.map(value => {
      const label = meta.levels.find(level => {
        return level.value === value
      }).label
      return `"${label}"`
    })
    const valueClause = makeNarrativeList(valueLabels, 'or')
    subpopTitleClause = ` (filtered to include only patients where ${meta.label} is ${valueClause})`
    subpopSubtitleClause = ` where [${subpopFilter.id}] = ${valueClause}`
  }

  let table
  let title
  let subtitle

  if (columnFilter) {
    const rowMeta = metadata[rowFilter.id]
    const colMeta = metadata[columnFilter.id]

    title = `${rowMeta.label}, by ${colMeta.label}${subpopTitleClause}, ${year}`
    subtitle = `[${rowFilter.id}] by [${columnFilter.id}]${subpopSubtitleClause}`

    const varName = columnFilter.id
    const varLevels = colMeta.levels.map(l => l.value)
    const byName = rowFilter.id
    const byLevels = rowMeta.levels.map(l => l.value)

    const results = survey.distribution(
      varName,
      varLevels,
      byName,
      byLevels,
      '__subpop__'
    )

    const height = rowFilter.values.length
    const width = columnFilter.values.length
    table = initializeTable(height, width)

    Object.keys(results).forEach((byLevel) => {
      Object.keys(results[byLevel]).forEach((varLevel, vi) => {
        const row = rowFilter.values.indexOf(byLevel)
        const col = columnFilter.values.indexOf(varLevel)
        table[row][col] = {
          ...suppressCell(results[byLevel][varLevel]),
          ...setCellHasResult(results[byLevel][varLevel]),
          benchmark: colMeta.levels[vi].benchmark
        }
      })
    })
  } else {
    const meta = metadata[rowFilter.id]
    title = `${meta.label}${subpopTitleClause}, ${year}`
    subtitle = `[${rowFilter.id}]${subpopSubtitleClause}`

    const varName = rowFilter.id
    const varLevels = meta.levels.map(l => l.value)
    const byName = '__zero__'
    const byLevels = ['0']

    const results = survey.distribution(
      varName,
      varLevels,
      byName,
      byLevels,
      '__subpop__'
    )

    const height = rowFilter.values.length
    const width = 1
    table = initializeTable(height, width)

    Object.keys(results['0']).forEach((varLevel, i) => {
      const row = rowFilter.values.indexOf(varLevel)
      table[row][0] = {
        ...suppressCell(results['0'][varLevel]),
        ...setCellHasResult(results['0'][varLevel]),
        benchmark: meta.levels[i].benchmark
      }
    })
  }

  return {
    table,
    title,
    subtitle
  }
}

function initializeTable (rows, columns) {
  const table = new Array(rows)

  for (let i = 0; i < rows; i++) {
    table[i] = new Array(columns).fill(0)
  }

  return table
}

/**
 * Converts an array to a string as it would appear in a narrative English
 * sentence, such as "x, y, and z".
 *
 * @param {Array} arr Array of strings to be converted to a narrative list.
 * @param {String} conjunction Word to use as the conjunction between the last
 *     two items of the list. Typically either "and" or "or".
 *
 * @return {String} The string containing the narrative list.
 */
function makeNarrativeList (arr, conjunction) {
  if (arr.length === 1) {
    return arr[0]
  } else if (arr.length === 2) {
    return arr.join(` ${conjunction} `)
  } else {
    return [
      arr.slice(0, -1).join(', '),
      arr[arr.length - 1]
    ].join(`, ${conjunction} `)
  }
}

function buildGraph ({ table: data, title, subtitle }, metadata, rowFilter, columnFilter) {
  if (data.length === 0) {
    return { type: 'lollipop', data: [], title: 'Make a selection at left to begin.' }
  }

  // plot single question
  if (data[0].length === 1) {
    const meta = metadata[rowFilter.id]
    return {
      type: 'lollipop',
      categories: meta.levels.map(l => l.label),
      categoryId: rowFilter.id,
      data: data,
      title: title,
      subtitle: subtitle,
      dependencies: [meta.pop]
    }
  }

  // plot comparison
  const rowMeta = metadata[rowFilter.id]
  const colMeta = metadata[columnFilter.id]

  return {
    type: 'lollipop',
    categories: rowMeta.levels.map(l => l.label),
    categoryId: rowFilter.id,
    subCategories: colMeta.levels.map(l => l.label),
    subCategoryId: columnFilter.id,
    data: data,
    title: title,
    subtitle: subtitle,
    dependencies: [
      rowMeta.pop,
      colMeta.pop
    ]
  }
}

function buildFootnotes (table, rowFilter, columnFilter, subpopFilter, year) {
  function doesHaveSubPop (filter) {
    return filter && filter.pop !== 'all patients'
  }

  function doesHaveBenchmark (row, col) {
    if (row == null && col == null) {
      return false
    }

    if (col) {
      return col.levels.some(l => l.benchmark != null)
    } else {
      return row.levels.some(l => l.benchmark != null)
    }
  }

  function buildSubpop (filter) {
    return doesHaveSubPop(filter) && {
      id: filter.id,
      population: filter.pop,
      text: `[${filter.id}] applies only to: ${filter.pop}`
    }
  }

  const rowId = rowFilter?.id
  const hasSuppressed = table.some(r => r.some(c => c.suppressed))

  const hasRowSubpop = doesHaveSubPop(rowFilter)
  const hasColSubpop = doesHaveSubPop(columnFilter)
  const hasFilterSubpop = doesHaveSubPop(subpopFilter)
  const hasSubpop = hasRowSubpop || hasColSubpop || hasFilterSubpop
  const hasBenchmark = doesHaveBenchmark(rowFilter, columnFilter)

  const subpopItems = []
  hasRowSubpop && subpopItems.push(buildSubpop(rowFilter))
  hasColSubpop && subpopItems.push(buildSubpop(columnFilter))
  hasFilterSubpop && subpopItems.push(buildSubpop(subpopFilter))

  return {
    weighting: `All dashboard results are calculated using data from the ${year} HCPS PUF. Individual records in the PUF are weighted so that estimates of percentages, population sizes, and confidence intervals are representative of the health center patient population. Sample sizes are not representative of the health center patient population; instead, they are unweighted counts of the number of survey responses. Population sizes are rounded to the nearest thousand. Confidence intervals are calculated to reflect the complex sample design of the survey.`,
    subpops: hasSubpop && {
      text: 'The estimates in this analysis are representative of a subset of the health center patient population, because one or more selected survey questions apply to a subset of the population:',
      items: subpopItems
    },
    suppressed: hasSuppressed && {
      symbol: '*',
      text: "Result has been suppressed. Results are suppressed when the percentage's denominator is based on fewer than 50 survey responses or when the relative standard error of the percentage is greater than 30%. Failure to meet these criteria is an indicator of poor statistical reliability."
    },
    variableName: rowId && `Text displayed in square brackets, such as [${rowId}], indicates a variable name from the ${year} HCPS PUF. The dashboard interface uses the variable names as an abbreviated way to identify survey questions. Analysts may wish to use the variable names to find the corresponding data in the PUF.`,
    benchmark: {
      hasBenchmark,
      symbol: '‡',
      text: 'National Benchmark: Corresponding estimate from the 2019 Consumer Assessment of Healthcare Providers and Systems (CAHPS®) Clinician & Group Survey Database (CG-CAHPS Database), where available. This estimate is intended to provide a benchmark for comparing the HCPS estimate to a broader population of U.S. medical practices. However, please be aware of the CG-CAHPS Database\'s limitations and take them into consideration when making any comparisons between the HCPS and the CG-CAHPS Database: The organizations that contribute data to the CG-CAHPS Database do so voluntarily, and a limited number of practices choose to participate. The organizations contributing data are not representative of all U.S. medical practices. Estimates based on these voluntarily submitted data sets might be biased as it is not possible to compute estimates of precision from them.'
    },
    compare2014: 'Comparing estimates from 2022 data to estimates from 2014 data is not recommended. Data collection procedures were changed during the 2022 survey because of the COVID-19 pandemic. These changes are a potential confounding factor in any differences observed between 2014 and 2022.'
  }
}
